import { useMemo, useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Button } from '../../components/atoms/Button'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { MaterialReceiveModal } from './MaterialReceiveModal'
import {
  useManufacturingMaterialPurchases,
  type MaterialAggregateDto
} from '../../../viewmodels/useManufacturingMaterialPurchases'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manufacturing/material-purchases — per-material summary with the
 * Receive/View flow that allocates a slice of a bare Manage Purchase
 * across raw materials linked to the supplier (writes mmp rows + raw
 * material stock movements atomically). Mirror of
 * /warehouse/product-purchases. Replaces the old Manufacturing >
 * Purchases screen (retired 2026-06-04).
 */
export function MaterialPurchasesPage(): JSX.Element {
  const vm = useManufacturingMaterialPurchases()
  const { can } = useAuth()
  const [receiveMaterial, setReceiveMaterial] = useState<MaterialAggregateDto | null>(null)
  // Empty string = "All products" (default).
  const [productFilter, setProductFilter] = useState<string>('')

  const canReceive = can(SCREENCODES.MANUFACTURINGMATERIALPURCHASES, 'edit')

  // Distinct owning products from the loaded aggregates — populates the
  // filter dropdown. Done in-memory so we don't need a separate products
  // fetch.
  const productOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const row of vm.materialAggregates) {
      if (!seen.has(row.product_id)) seen.set(row.product_id, row.product_name)
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [vm.materialAggregates])

  const filteredAggregates = useMemo(
    () => (productFilter === ''
      ? vm.materialAggregates
      : vm.materialAggregates.filter((r) => r.product_id === productFilter)),
    [vm.materialAggregates, productFilter]
  )

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Material Purchases</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            One row per raw material. Receive allocates a Manage Purchase across the supplier&apos;s
            materials and lands it as stock.
          </p>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[14rem]">
          <Select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            aria-label="Filter by product"
          >
            <option value="">All products</option>
            {productOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </div>
      </header>

      {vm.isAggregatesLoading ? (
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      ) : vm.materialAggregates.length === 0 ? (
        <Placeholder>No raw materials yet — add one on Manufacturing &gt; Raw Materials first.</Placeholder>
      ) : filteredAggregates.length === 0 ? (
        <Placeholder>No materials for the selected product.</Placeholder>
      ) : (
        <MaterialSummaryTable
          items={filteredAggregates}
          canReceive={canReceive}
          onReceive={(row) => setReceiveMaterial(row)}
        />
      )}

      <MaterialReceiveModal
        open={receiveMaterial !== null}
        material={receiveMaterial}
        onClose={() => {
          setReceiveMaterial(null)
          vm.reloadAggregates()
        }}
        getOptions={vm.getReceiveOptionsByMaterial}
        onAllocate={vm.allocate}
      />
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-10 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}

function MaterialSummaryTable({
  items,
  canReceive,
  onReceive
}: {
  readonly items: ReadonlyArray<MaterialAggregateDto>
  readonly canReceive: boolean
  readonly onReceive: (row: MaterialAggregateDto) => void
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Product</Th>
            <Th>Material</Th>
            <Th>Unit</Th>
            <Th className="text-right">In stock</Th>
            <Th className="text-right">Pending invoices</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const receiveDisabled = !canReceive || !row.link_active
            const title = !canReceive
              ? ''
              : !row.link_active
                ? 'No supplier linked to this material on Manage > Material Suppliers.'
                : ''
            const label = row.pending_invoice_count === 0 ? 'View' : 'Receive'
            return (
              <tr key={row.raw_material_id} className="border-t border-border">
                <Td className="text-text-secondary">{row.product_name}</Td>
                <Td>{row.raw_material_name}</Td>
                <Td>{row.unit_label ?? <span className="text-text-secondary">—</span>}</Td>
                <Td className="text-right whitespace-nowrap font-mono">{row.in_stock_units.toLocaleString('en-IN')}</Td>
                <Td className="text-right whitespace-nowrap font-mono">
                  {row.pending_invoice_count === 0
                    ? <span className="text-text-secondary">—</span>
                    : <span className="text-charcoal">{row.pending_invoice_count}</span>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canReceive ? (
                    <Button size="sm" onClick={() => onReceive(row)} disabled={receiveDisabled} title={title}>
                      {label}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
